import { apiFetch } from "@/client/lib/api";
import type { StaffRole, TenantRole, UserKind } from "@/shared/types";

/**
 * Typed client for the team-management endpoints (the customer "Your team"
 * page): list the org's members, list pending invites, and invite a teammate.
 * All requests are tenant-scoped server-side (RLS for users, explicit tenant
 * filter for invites), so the client never passes a tenant id.
 */

export interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  kind: UserKind;
  tenantId: string | null;
  tenantRole: TenantRole | null;
  staffRole: StaffRole | null;
  status: string;
  mfaEnabled: boolean;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: TenantRole;
  expiresAt: string;
  createdAt: string;
}

export interface CreateInviteInput {
  email: string;
  role: TenantRole;
}

export interface CreatedInvite {
  id: string;
  email: string;
  role: TenantRole;
  expiresAt: string;
}

export interface MemberSshKey {
  id: string;
  label: string | null;
  keyType: string;
  fingerprint: string;
  comment: string | null;
  createdAt: string;
}

export interface AddMemberKeyInput {
  publicKey: string;
  label?: string;
}

export const teamApi = {
  listMembers: () => apiFetch<{ users: TeamMember[] }>("/users"),
  listInvites: () => apiFetch<{ invites: PendingInvite[] }>("/invites"),
  createInvite: (input: CreateInviteInput) =>
    apiFetch<CreatedInvite>("/invites", { method: "POST", json: input }),
  removeMember: (id: string) => apiFetch<void>(`/users/${id}`, { method: "DELETE" }),
  listMemberKeys: (userId: string) =>
    apiFetch<{ keys: MemberSshKey[] }>(`/users/${encodeURIComponent(userId)}/ssh-keys`),
  addMemberKey: (userId: string, input: AddMemberKeyInput) =>
    apiFetch<MemberSshKey>(`/users/${encodeURIComponent(userId)}/ssh-keys`, {
      method: "POST",
      json: input,
    }),
  removeMemberKey: (userId: string, keyId: string) =>
    apiFetch<void>(`/users/${encodeURIComponent(userId)}/ssh-keys/${encodeURIComponent(keyId)}`, {
      method: "DELETE",
    }),
};
